import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../../context/AuthContext';
import Layout from '../../../components/layout/Layout';
import { Coins, History, Check, Star, PlusCircle, MinusCircle, Clock, UserPlus, Info, Cake, Sparkles, ShieldCheck, Percent, DollarSign } from 'lucide-react';
import type { WhatsAppClient } from '../../../types/user';
import DateTimeDisplay from '../../../components/common/DateTimeDisplay';
import { useError } from '../../../context/ErrorContext';
import ClientContactInfo from '../../../components/common/ClientContactInfo';
import SearchInput from '../../../components/common/SearchInput';
import { isPro as checkIsPro } from '../../../utils/planPermissions';
import { ProLocked } from '../../../components/pro/ProLocked';
import { clientsApi } from '../../../api/clientsApi';
import { pctCashbackCliente } from '../../../utils/cashbackEspecial';

// --- HELPERS ---
const normalizeSearchTerm = (term: string): string => {
  if (!term) return '';
  if (/[a-zA-Z]/.test(term)) return term;
  let cleaned = term.replace(/\D/g, '');
  if (cleaned.length >= 12 && cleaned.startsWith('52')) { return cleaned.substring(2); }
  if (cleaned.length >= 11 && cleaned.startsWith('1')) { return cleaned.substring(1); }
  return cleaned || term;
};

interface PointsRecharge {
    id: string;
    clientId: string;
    clientName: string | null;
    clientPhone: string;
    points: number;
    previousPoints: number;
    newPoints: number;
    businessId: string;
    createdAt: Date;
    operation: 'add' | 'subtract';
}

// --- COMPONENT ---

const RechargePointsPage: React.FC = () => {
    const { currentUser } = useAuth();
    const { addError, handleFirebaseError, clearErrors, addValidationError } = useError();

    // --- State ---
    const [selectedClient, setSelectedClient] = useState<WhatsAppClient | null>(null);
    const [inputValue, setInputValue] = useState(''); 
    const [operation, setOperation] = useState<'add' | 'subtract'>('add');
    const [redeemLoading, setRedeemLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [recentRecharges, setRecentRecharges] = useState<PointsRecharge[]>([]);
    // Gestión ligera de "Operaciones Recientes": filtro rápido client-side (los volúmenes
    // de ajustes manuales son chicos; no amerita paginación server-side como Últimas Visitas).
    const [opsFilter, setOpsFilter] = useState<'all' | 'add' | 'subtract'>('all');
    const [opsSearch, setOpsSearch] = useState('');
    const [isDoublePointsApplied, setIsDoublePointsApplied] = useState(false);
    const [notifyWhatsApp, setNotifyWhatsApp] = useState(true);
    // Chips de monto rápido (auto-activados por datos del negocio). Plan: docs/lealify-plan-chips-recarga.md
    const [quickChips, setQuickChips] = useState<number[]>([]);
    
    const hasProAccess = checkIsPro(currentUser?.subscription);
    const showPaywall = !hasProAccess;

    // --- CONFIGURACIÓN DEL NEGOCIO ---
    const birthdayMode = currentUser?.birthdayPointsMode || 'disabled';
    const loyaltyMode = currentUser?.loyaltyMode || 'visits';
    const cashbackGeneral = currentUser?.cashbackPercentage || 10;
    // Cashback especial por etiqueta (17-oct-2026): esta pantalla calcula el abono aquí (ajuste manual),
    // así que aplica la misma regla que el servidor en el check-in: gana el mayor.
    const { pct: cashbackPercentage, especial: cashbackEsEspecial } = pctCashbackCliente(currentUser, selectedClient?.tags, cashbackGeneral);

    // --- Fetch Recent Recharges ---
    const fetchRecentRecharges = useCallback(async () => {
        if (!currentUser?.businessId) return;
        try {
            if(clearErrors) clearErrors();
            const list = await clientsApi.recentAdjustments(50);
            setRecentRecharges(list as PointsRecharge[]);
        } catch (error) {
            console.error('Error fetchRecentRecharges:', error);
            if(handleFirebaseError) handleFirebaseError(error, 'fetch-recent-recharges');
        }
    }, [currentUser?.businessId, clearErrors, handleFirebaseError]);

    useEffect(() => { fetchRecentRecharges(); }, [fetchRecentRecharges]);

    // --- Chips de monto rápido (100% client-side, se auto-activan por datos) ---
    // Reusa el índice businessId+createdAt (NO se filtra en el where → sin índice nuevo);
    // el filtrado de manuales/90d/elegibilidad ocurre en JS. Ver docs/lealify-plan-chips-recarga.md
    // Chips de monto rápido: dependían de leer 300 pointsRecharges de Firestore. Diferido en base
    // (sin endpoint de montos de recarga aún) → sin chips por ahora. Ver docs/lealify-plan-chips-recarga.md
    useEffect(() => { setQuickChips([]); }, [currentUser?.businessId, loyaltyMode]);

    const fetchClientById = useCallback(async (clientId: string) => {
        if (!currentUser?.businessId) return null;
        try {
            return await clientsApi.getClient(clientId);
        } catch (error) { console.error("Error fetching client details:", error); }
        return null;
    }, [currentUser?.businessId]);

    const handleClientSelect = async (clientId: string) => {
        if (!clientId) return;
        if (clearErrors) clearErrors();
        const clientData = await fetchClientById(clientId);
        if (clientData) {
            setSelectedClient(clientData);
            setInputValue('');
            setOperation('add');
            setIsDoublePointsApplied(false);
        } else {
            if (addError) addError("No se pudo cargar los detalles del cliente", undefined, "client-not-found");
            setSelectedClient(null);
        }
    };

    const isBirthdayToday = useMemo(() => {
        if (!selectedClient?.birthDay || selectedClient.birthMonth === null || selectedClient.birthMonth === undefined) return false;
        const today = new Date();
        const currentDay = today.getDate();
        // clientsApi entrega birthMonth 1-12; getMonth() es 0-11. Antes aceptaba ambos y daba el x2
        // también a quien cumplió el mismo día del mes ANTERIOR (16-sep-2026).
        const currentMonth = today.getMonth() + 1;
        return selectedClient.birthDay === currentDay && selectedClient.birthMonth === currentMonth;
    }, [selectedClient]);

    const pointsToSave = useMemo(() => {
        const rawValue = parseFloat(inputValue) || 0;
        let calculated = rawValue;

        if (loyaltyMode === 'cashback' && operation === 'add') {
            calculated = parseFloat((rawValue * (cashbackPercentage / 100)).toFixed(2));
        } else {
            calculated = Math.floor(rawValue);
        }

        if (operation === 'add' && isBirthdayToday && birthdayMode === 'automatic') {
            calculated = parseFloat((calculated * 2).toFixed(2));
        }

        if (operation === 'add' && isDoublePointsApplied) {
            calculated = parseFloat((calculated * 2).toFixed(2));
        }

        return calculated;
    }, [inputValue, loyaltyMode, cashbackPercentage, operation, isBirthdayToday, birthdayMode, isDoublePointsApplied]);

    const handlePointsInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        if (loyaltyMode === 'cashback') {
             if (/^\d*\.?\d*$/.test(val)) {
                 setInputValue(val);
             }
        } else {
             setInputValue(val.replace(/\D/g, ''));
        }
        setIsDoublePointsApplied(false); 
    };

    const handleApplyDouble = () => {
        if (parseFloat(inputValue) > 0) {
            setIsDoublePointsApplied(true);
        }
    };

    const handlePointsOperation = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!currentUser?.businessId) { if(addError) addError("No se pudo identificar al usuario o negocio.", undefined, "auth"); return; };
        if (!selectedClient) { if(addValidationError) addValidationError('Por favor, busca y selecciona un cliente primero.', 'rechargePoints'); return; }

        setRedeemLoading(true); if(clearErrors) clearErrors(); setSuccess(false);
        
        const finalPoints = pointsToSave;

        if (finalPoints <= 0) { 
            if(addValidationError) addValidationError(loyaltyMode === 'cashback' ? 'El monto debe generar al menos 0.01 puntos.' : 'Cantidad inválida.', 'rechargePoints'); 
            setRedeemLoading(false); return; 
        }

        let newTotalPoints: number; const currentPoints = selectedClient.points ?? 0;
        
        if (operation === 'add') { 
            newTotalPoints = parseFloat((currentPoints + finalPoints).toFixed(2)); 
        } else { 
            const pointsToSubtract = parseFloat(inputValue) || 0;
            if (currentPoints < pointsToSubtract) { 
                if(addValidationError) addValidationError('Saldo insuficiente.', 'rechargePoints'); 
                setRedeemLoading(false); return; 
            } 
            newTotalPoints = parseFloat((currentPoints - pointsToSubtract).toFixed(2)); 
        }

        // Ajuste ATÓMICO server-side. add → suma los puntos calculados (cashback/cumpleaños/x2 ya
        // aplicados en pointsToSave); subtract → resta el valor crudo ingresado. points firmado.
        const signedPoints = operation === 'add' ? finalPoints : -(parseFloat(inputValue) || 0);
        try {
            const out = await clientsApi.adjust({ contactId: selectedClient.id, points: signedPoints, note: 'Gestión de puntos', notifyWhatsApp });
            const balance = typeof out.balance === 'number' ? out.balance : newTotalPoints;
            setSelectedClient(prev => prev ? { ...prev, points: balance } : prev);

            setInputValue(''); setOperation('add'); setIsDoublePointsApplied(false); setNotifyWhatsApp(true);
            setSuccess(true); fetchRecentRecharges();
            setTimeout(() => { setSuccess(false); }, 3000);
        } catch (err: any) {
            console.error("Operación de puntos falló:", err);
            if(handleFirebaseError) { handleFirebaseError(err, 'rechargePoints'); }
        } finally { setRedeemLoading(false); }
    };


    // Operaciones recientes filtradas (tipo + búsqueda por nombre/teléfono, client-side)
    const filteredRecharges = useMemo(() => {
        const term = opsSearch.trim().toLowerCase();
        return recentRecharges.filter((r) => {
            if (opsFilter !== 'all' && r.operation !== opsFilter) return false;
            if (!term) return true;
            return (r.clientName || '').toLowerCase().includes(term) ||
                (r.clientPhone || '').replace(/\D/g, '').includes(term.replace(/\D/g, '') || term);
        });
    }, [recentRecharges, opsFilter, opsSearch]);
    
    if (showPaywall) {
         return (
            <Layout>
                 <div className="space-y-6 px-2 sm:px-0">
                    <div className="animate-fade-in-up" style={{animationDelay: '0.1s'}}>
                        <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center"><Coins className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-accent" /> Gestionar Puntos de Cliente</h1>
                        <p className="mt-1 text-sm text-content-2">Busca un cliente y agrega o resta puntos a su cuenta</p>
                    </div>
                    <ProLocked feature="recharge-points" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6 px-2 sm:px-0"> 
                <div className="animate-fade-in-up" style={{animationDelay: '0.1s'}}>
                    <h1 className="text-2xl font-bold text-content flex items-center"><Coins className="h-6 w-6 mr-2 text-accent" /> Gestionar Puntos de Cliente</h1>
                    <p className="mt-1 text-sm text-content-2">Busca un cliente y agrega o resta puntos a su cuenta</p>
                </div>
                
                {success && ( <div className="bg-green-50 dark:bg-green-500/10 border-l-4 border-green-400 p-4 animate-fade-in-up" style={{animationDelay: '0.15s'}}><div className="flex"><div className="flex-shrink-0"><Check className="h-5 w-5 text-green-400" /></div><div className="ml-3"><p className="text-sm text-green-700 dark:text-green-300">Operación realizada exitosamente. Saldo actualizado.</p></div></div></div> )}

                <div className="relative bg-surface shadow sm:rounded-lg animate-fade-in-up" style={{animationDelay: '0.15s', zIndex: 30}}>
                    <div className="px-4 py-5 sm:p-6">
                        <label htmlFor="search" className="block text-sm font-medium text-content-2 mb-1">Buscar por nombre o teléfono</label>
                        <SearchInput
                            onSearch={(term) => { const cleanTerm = normalizeSearchTerm(term || ''); handleClientSelect(cleanTerm); }}
                            disabled={!currentUser?.businessId || redeemLoading}
                            preventClientNavigation={true}
                            placeholder="Buscar cliente por nombre o teléfono"
                            showResults={true}
                            showMinLengthWarning={true}
                        />
                        <p className="mt-2 text-xs text-content-3 flex items-center"><Info className="h-3 w-3 mr-1" />Nota: Al buscar por teléfono, ingresa solo los 10 dígitos.</p>
                    </div>
                </div>

                <div className="relative bg-surface shadow sm:rounded-lg animate-fade-in-up" style={{animationDelay: '0.15s', zIndex: 20}}>
                    <form onSubmit={handlePointsOperation}>
                        <div className="px-4 py-5 sm:p-6">
                            <h3 className="text-lg font-medium text-content mb-4">Detalles del Cliente y Operación</h3>
                            
                            {selectedClient ? (
                                <div className="space-y-6">
                                     <div className="animate-fade-in-up" style={{animationDelay: '0.15s'}}>
                                        <div className="bg-surface-2 p-4 rounded-md border border-line mb-4">
                                            <div className="flex items-start justify-between flex-wrap gap-2">
                                                <div><ClientContactInfo clientId={selectedClient.id} clientName={selectedClient.visitorName} phoneNumber={selectedClient.phoneNumber} countryCode={selectedClient.countryCode}/></div>
                                                <div className="text-right ml-4 flex-shrink-0">
                                                    <div className="text-sm text-content-2">Saldo actual</div>
                                                    <div className="text-xl font-semibold text-green-600 dark:text-green-400 flex items-center justify-end">
                                                        {loyaltyMode === 'cashback' ? <DollarSign className="h-5 w-5 mr-0.5" /> : <Star className="h-5 w-5 mr-1" />}
                                                        {selectedClient.points % 1 !== 0 
                                                            ? selectedClient.points.toFixed(2) 
                                                            : selectedClient.points
                                                        }
                                                        {loyaltyMode !== 'cashback' && <span className="ml-1 text-sm">pts</span>}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {isBirthdayToday && (
                                        <div className="mb-6 bg-pink-50 dark:bg-pink-500/10 border-l-4 border-pink-400 p-4 rounded-r-md animate-fade-in-up">
                                            <div className="flex items-start">
                                                <div className="flex-shrink-0"><Cake className="h-6 w-6 text-pink-500 animate-pop" /></div>
                                                <div className="ml-3 w-full">
                                                    <div className="flex justify-between items-start">
                                                        <h3 className="text-sm font-medium text-pink-800 dark:text-pink-300">¡Es el cumpleaños de {selectedClient.visitorName || 'este cliente'}!</h3>
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-surface text-pink-800 dark:text-pink-300 border border-pink-200 shadow-sm">
                                                            <ShieldCheck className="w-3 h-3 mr-1" /> Panel Admin
                                                        </span>
                                                    </div>
                                                    <div className="mt-2 text-sm text-pink-700 dark:text-pink-300"><p>No olvides felicitarlo.</p></div>
                                                    
                                                    {birthdayMode === 'automatic' && operation === 'add' && (
                                                        <div className="mt-3 flex items-center text-sm font-medium text-pink-700 dark:text-pink-300 bg-pink-100 dark:bg-pink-500/20 px-3 py-2 rounded-md border border-pink-200">
                                                            <Sparkles className="h-4 w-4 mr-2" />
                                                            Se aplicarán puntos dobles automáticamente (x2).
                                                        </div>
                                                    )}

                                                    {birthdayMode === 'manual' && operation === 'add' && (
                                                        <div className="mt-3">
                                                            {isDoublePointsApplied ? (
                                                                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-300 border border-green-200">
                                                                    <Check className="h-3 w-3 mr-1" /> Puntos dobles aplicados
                                                                </span>
                                                            ) : (
                                                                <button type="button" onClick={handleApplyDouble} className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-full shadow-sm text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 transition-colors">
                                                                    <Star className="h-3 w-3 mr-1" /> Aplicar Puntos Dobles (x2)
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="animate-fade-in-up" style={{animationDelay: '0.15s'}}>
                                            <label htmlFor="points" className="block text-sm font-medium text-content-2 mb-1">
                                                {loyaltyMode === 'cashback' && operation === 'add' ? 'Monto de Venta ($)' : 'Cantidad de Puntos'}
                                            </label>
                                            
                                            <div className="relative rounded-md shadow-sm max-w-xs">
                                                {loyaltyMode === 'cashback' && operation === 'add' && (
                                                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                        <span className="text-content-3 sm:text-sm">$</span>
                                                    </div>
                                                )}
                                                <input 
                                                    type="number"
                                                    id="points" 
                                                    min="0.01" 
                                                    step="0.01"
                                                    required 
                                                    value={inputValue} 
                                                    onChange={handlePointsInputChange} 
                                                    className={`block w-full rounded-md border-line focus:border-blue-500 focus:ring-accent sm:text-sm ${loyaltyMode === 'cashback' && operation === 'add' ? 'pl-7' : 'px-3'} py-2`}
                                                    disabled={redeemLoading}
                                                    placeholder={loyaltyMode === 'cashback' && operation === 'add' ? "0.00" : "1"}
                                                />
                                            </div>

                                            {/* Chips de monto rápido (solo negocios cuyos datos lo ameritan; ver plan) */}
                                            {operation === 'add' && quickChips.length > 0 && (
                                                <div className="flex flex-wrap gap-2 mt-2">
                                                    {quickChips.map(n => (
                                                        <button type="button" key={n}
                                                            onClick={() => { setInputValue(String(n)); setIsDoublePointsApplied(false); }}
                                                            className="px-3 py-1 rounded-full border border-line text-sm text-content-2 hover:bg-surface-2 transition-colors">
                                                            {n}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}

                                            {loyaltyMode === 'cashback' && operation === 'add' && inputValue && (
                                                <p className="mt-2 text-sm text-green-600 dark:text-green-400 flex items-center animate-fade-in">
                                                    <Percent className="w-4 h-4 mr-1 shrink-0" />
                                                    <span>Cashback {cashbackPercentage}%{cashbackEsEspecial ? ' (especial)' : ''}: se abonarán <strong>${pointsToSave}</strong> a su monedero.</span>
                                                </p>
                                            )}
                                    </div>
                                    
                                    <div className="animate-fade-in-up" style={{animationDelay: '0.15s'}}>
                                            <label className="block text-sm font-medium text-content-2 mb-2">Tipo de Operación</label>
                                            <div className="flex items-center space-x-6">
                                                <div className="flex items-center"><input id="operation-add" name="operation" type="radio" value="add" checked={operation === 'add'} onChange={() => setOperation('add')} className="focus:ring-accent h-4 w-4 text-accent border-line" disabled={redeemLoading}/><label htmlFor="operation-add" className="ml-2 block text-sm text-content-2 flex items-center cursor-pointer"><PlusCircle className="h-4 w-4 mr-1 text-green-500" /> Agregar / Venta</label></div>
                                                <div className="flex items-center"><input id="operation-subtract" name="operation" type="radio" value="subtract" checked={operation === 'subtract'} onChange={() => setOperation('subtract')} className="focus:ring-red-500 h-4 w-4 text-red-600 dark:text-red-400 border-line" disabled={redeemLoading}/><label htmlFor="operation-subtract" className="ml-2 block text-sm text-content-2 flex items-center cursor-pointer"><MinusCircle className="h-4 w-4 mr-1 text-red-500" /> Restar / Canje</label></div>
                                            </div>
                                    </div>

                                    {/* Las restas nunca avisan (backend solo notifica sumas) → el checkbox solo aplica al agregar */}
                                    {operation === 'add' && (
                                        <div className="animate-fade-in-up flex items-center" style={{animationDelay: '0.15s'}}>
                                            <input id="notify-whatsapp" type="checkbox" checked={notifyWhatsApp} onChange={(e) => setNotifyWhatsApp(e.target.checked)} disabled={redeemLoading} className="h-4 w-4 rounded text-accent focus:ring-accent border-line" />
                                            <label htmlFor="notify-whatsapp" className="ml-2 text-sm text-content-2 cursor-pointer">
                                                Avisar al cliente por WhatsApp
                                                <span className="block text-xs text-content-3">Desmárcalo si es una corrección interna de saldo.</span>
                                            </label>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center text-content-3 py-8"><UserPlus className="mx-auto h-12 w-12 text-content-3" /><p className="mt-2 text-sm">Busca y selecciona un cliente para gestionar sus puntos.</p></div>
                            )}
                        </div>
                        
                        {selectedClient && (
                             <div className="px-4 py-4 sm:px-6 bg-surface-2 border-t border-line animate-fade-in-up" style={{animationDelay: '0.15s'}}>
                                <div className="flex justify-end">
                                    <button type="submit" disabled={redeemLoading || !inputValue || parseFloat(inputValue) <= 0} className={`inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${operation === 'add' ? 'bg-blue-600 hover:bg-blue-700 focus:ring-accent' : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'} focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed`}>
                                            {redeemLoading ? 'Procesando...' : operation === 'add' ? (
                                                <>
                                                    <PlusCircle className="h-5 w-5 mr-2 -ml-1" /> 
                                                    {isBirthdayToday && birthdayMode === 'automatic' ? (
                                                        <span>Agregar {pointsToSave} pts (x2 Auto)</span>
                                                    ) : (
                                                        <span>Agregar {pointsToSave} pts</span>
                                                    )}
                                                </>
                                            ) : (
                                                <><MinusCircle className="h-5 w-5 mr-2 -ml-1" /> Restar {parseFloat(inputValue) || 0} pts</>
                                            )}
                                    </button>
                                </div>
                            </div>
                        )}
                    </form>
                </div>

                <div className="relative bg-surface shadow sm:rounded-lg overflow-hidden animate-fade-in-up" style={{animationDelay: '0.15s', zIndex: 10}}>
                    <div className="px-4 py-5 sm:p-6">
                        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                            <h2 className="text-lg font-medium text-content flex items-center"><History className="h-5 w-5 mr-2" /> Operaciones Recientes</h2>
                            <div className="flex flex-wrap items-center gap-2">
                                <input
                                    type="text"
                                    value={opsSearch}
                                    onChange={(e) => setOpsSearch(e.target.value)}
                                    placeholder="Filtrar por nombre o teléfono"
                                    className="rounded-md border border-line bg-surface-2 px-3 py-1.5 text-sm text-content outline-none focus:border-accent/50 w-52"
                                />
                                <div className="flex rounded-md border border-line overflow-hidden text-xs">
                                    {([['all','Todas'],['add','Sumas'],['subtract','Restas']] as const).map(([k, lbl]) => (
                                        <button key={k} type="button" onClick={() => setOpsFilter(k)}
                                            className={`px-3 py-1.5 font-medium transition-colors ${opsFilter === k ? 'bg-accent/15 text-accent' : 'bg-surface text-content-2 hover:bg-surface-2'}`}>
                                            {lbl}
                                        </button>
                                    ))}
                                </div>
                                <span className="text-xs text-content-3">{filteredRecharges.length} de {recentRecharges.length}</span>
                            </div>
                        </div>
                        <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                            {filteredRecharges.length === 0 ? ( <p className="text-sm text-content-3 text-center py-4">{recentRecharges.length === 0 ? 'No hay operaciones recientes.' : 'Sin resultados con ese filtro.'}</p> )
                               : ( filteredRecharges.map((recharge, index) => (
                                    <div key={recharge.id} className={`border rounded-lg p-3 hover:bg-surface-2 transition-colors duration-150 ${recharge.operation === 'add' ? 'border-green-200 bg-green-50 dark:bg-green-500/10' : 'border-red-200 bg-red-50 dark:bg-red-500/10'} animate-fade-in-up`} style={{animationDelay: `${0.7 + Math.min(index, 6) * 0.05}s`}}>
                                            <div className="flex justify-between items-start gap-4">
                                                <div className="flex-1 min-w-0">
                                                    <ClientContactInfo
                                                        clientId={recharge.clientId}
                                                        clientName={recharge.clientName || 'Cliente sin nombre'}
                                                        phoneNumber={recharge.clientPhone}
                                                        countryCode={null}
                                                        iconSize="sm"
                                                        nameAsLink
                                                    />

                                                    <p className="text-xs text-content-3 mt-1 flex items-center">
                                                        Anterior: 
                                                        {loyaltyMode === 'cashback' ? (
                                                            <span className="ml-1">${(recharge.previousPoints ?? 0).toFixed(2)}</span>
                                                        ) : (
                                                            <span className="ml-1">{Math.floor(recharge.previousPoints ?? 0)} pts</span>
                                                        )}
                                                    </p>
                                                </div>
                                                <div className="text-right flex-shrink-0">
                                                    <div className={`text-sm font-semibold flex items-center justify-end ${recharge.operation === 'add' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                                        {recharge.operation === 'add' ? '+' : '-'}
                                                        {loyaltyMode === 'cashback' ? (
                                                            <>
                                                                <DollarSign className="h-3 w-3 mr-0.5" />
                                                                {recharge.points.toFixed(2)}
                                                            </>
                                                        ) : (
                                                            <>
                                                                {Math.floor(recharge.points)} <Star className="h-3 w-3 ml-0.5" />
                                                            </>
                                                        )}
                                                    </div>
                                                    
                                                    <div className="text-xs text-content-3 mt-1"><Clock className="h-3 w-3 inline mr-1 relative -top-px" /><DateTimeDisplay date={recharge.createdAt} format="relativeShort" /></div>
                                                    
                                                    <div className="text-xs text-content-2 font-medium mt-1 flex items-center justify-end">
                                                        Nuevo: 
                                                        {loyaltyMode === 'cashback' ? (
                                                            <span className="ml-1">${(recharge.newPoints ?? 0).toFixed(2)}</span>
                                                        ) : (
                                                            <span className="ml-1">{Math.floor(recharge.newPoints ?? 0)} pts</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default RechargePointsPage;